import apiClient from './client';
import { Feedback } from '@/types';

export interface FeedbackListParams {
  page?: number;
  limit?: number;
  search?: string;
  isRead?: boolean;
  isActive?: boolean;
}

export interface FeedbackListResponse {
  data: Feedback[];
  total: number;
  page: number;
  limit: number;
}

export interface FeedbackReplyPayload {
  content_balas: string;
}

export interface MessageResponse {
  message: string;
}

type RawFeedbackItem = {
  id?: number;
  kmj_user_id?: string;
  content?: string;
  content_balas?: string | null;
  is_read?: boolean;
  is_active?: boolean;
  created_at?: string | { date?: string; timezone?: string; timezone_type?: number } | null;
};

type RawFeedbackResponse = {
  content?: RawFeedbackItem[];
  total_item?: number;
  page?: number;
  total_page?: number;
};

const normalizeDate = (value: RawFeedbackItem['created_at']): string | undefined => {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object' && typeof value.date === 'string') {
    return value.date;
  }

  return undefined;
};

const normalizeItem = (item: RawFeedbackItem): Feedback => ({
  id: Number(item.id || 0),
  kmjUserId: item.kmj_user_id,
  content: item.content,
  contentBalas: item.content_balas ?? null,
  isRead: !!item.is_read,
  isActive: !!item.is_active,
  createdAt: normalizeDate(item.created_at),
});

export const feedbackApi = {
  getAll: async (params: FeedbackListParams = {}): Promise<FeedbackListResponse> => {
    const { page = 1, limit = 10, search = '', isRead, isActive } = params;

    const filter: Record<string, string | number | boolean> = {};
    if (search.trim()) {
      filter.content = search.trim();
    }
    if (typeof isRead === 'boolean') {
      filter.is_read = isRead;
    }
    if (typeof isActive === 'boolean') {
      filter.is_active = isActive;
    }

    const response = await apiClient.get<RawFeedbackResponse>('/api/feedback.json', {
      params: {
        page,
        limit,
        ...(Object.keys(filter).length ? { filter } : {}),
      },
    });

    const payload = response.data || {};
    const items = Array.isArray(payload.content) ? payload.content : [];

    return {
      data: items.map(normalizeItem),
      total: Number(payload.total_item || 0),
      page: Number(payload.page || page),
      limit,
    };
  },

  reply: async (id: number, data: FeedbackReplyPayload): Promise<MessageResponse> => {
    const response = await apiClient.patch(`/api/feedback/${id}/reply`, data);
    return response.data;
  },
};
